import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Code, Terminal, DollarSign, Users, Sparkles } from 'lucide-react';

const ThePromptAssistStory = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gradient-to-b from-background to-background/95"
    >
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Link 
          to="/blog" 
          className="inline-flex items-center text-muted-foreground hover:text-purple-400 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-16">
              <div className="relative h-96 w-full mb-8 rounded-xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop" 
                  alt="Prompt Engineering" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm mb-4">
                    Our Story
                  </span>
                  <h1 className="text-4xl font-bold mb-4">The PromptAssist Story: Building the Future of Prompt Engineering</h1>
                  <div className="flex items-center text-sm text-white/80">
                    <span>Published on March 20, 2024</span>
                    <span className="mx-2">•</span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      10 min read
                    </span>
                  </div>
                </div>
              </div>

              <div className="prose prose-invert max-w-none">
                <p className="text-lg mb-6">
                  It all started with a simple tweet. A founder was looking to hire prompt engineers for their AI project, but couldn't find the right talent. That moment sparked an idea: what if we could democratize prompt engineering and make it accessible to everyone?
                </p>

                <div className="my-8 p-6 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Key Takeaways</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Democratizing prompt engineering for everyone
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Affordable pricing starting at just $5
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-400 mr-2">•</span>
                      Helping users break through creative blocks
                    </li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">The Spark of Inspiration</h2>
                <p className="mb-6">
                  The journey began when we noticed a growing trend on social media. Talented prompt engineers were creating amazing projects, but there was no centralized platform to connect them with businesses or help others learn their craft. We saw an opportunity to build something special - a platform that would make prompt engineering accessible to everyone, not just the experts.
                </p>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop" 
                    alt="Creative Process" 
                    className="w-full rounded-xl"
                  />
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    The creative process behind building PromptAssist
                  </p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Our Mission</h2>
                <p className="mb-6">
                  At PromptAssist, we believe that everyone should have access to high-quality prompt engineering. Whether you're a beginner looking to learn the basics or an experienced developer trying to optimize your prompts, our platform is designed to help you succeed.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h4 className="text-lg font-semibold mb-3">For Beginners</h4>
                    <p className="text-muted-foreground">
                      Start with our guided prompt creation process and learn the fundamentals of effective prompt engineering.
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6">
                    <h4 className="text-lg font-semibold mb-3">For Experts</h4>
                    <p className="text-muted-foreground">
                      Access advanced tools and techniques to take your prompt engineering skills to the next level.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">The PromptAssist Difference</h2>
                <div className="bg-white/5 rounded-xl p-6 my-8">
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Sparkles className="h-6 w-6 text-purple-400 mr-3 mt-1" />
                      <div>
                        <h4 className="text-lg font-semibold mb-2">Affordable Pricing</h4>
                        <p className="text-muted-foreground">
                          Starting at just $5 for 20 credits, our pricing is designed to be accessible to everyone. No hidden fees, no complicated plans - just straightforward pricing that makes sense.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Code className="h-6 w-6 text-purple-400 mr-3 mt-1" />
                      <div>
                        <h4 className="text-lg font-semibold mb-2">Advanced Tools</h4>
                        <p className="text-muted-foreground">
                          Our AI-powered tools help you create, optimize, and refine your prompts. Whether you're starting from scratch or looking to improve an existing prompt, we've got you covered.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Users className="h-6 w-6 text-purple-400 mr-3 mt-1" />
                      <div>
                        <h4 className="text-lg font-semibold mb-2">Community Support</h4>
                        <p className="text-muted-foreground">
                          Join our growing community of prompt engineers. Share your experiences, learn from others, and grow together.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Success Stories</h2>
                <div className="space-y-8 my-8">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-3">From Zero to Hero</h3>
                    <p className="mb-4">
                      Meet Sarah, a content creator who went from struggling with basic prompts to creating viral AI-generated content. "PromptAssist helped me understand the art of prompt engineering. Now I can create engaging content in minutes instead of hours."
                    </p>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>3 months using PromptAssist</span>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-3">The Startup Success</h3>
                    <p className="mb-4">
                      A small startup used PromptAssist to optimize their customer support prompts. "We reduced our response time by 60% and improved customer satisfaction scores. The ROI has been incredible."
                    </p>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>6 months using PromptAssist</span>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Looking to the Future</h2>
                <p className="mb-6">
                  We're just getting started. Our roadmap includes exciting new features like collaborative prompt building, advanced analytics, and integration with more AI platforms. We're committed to making PromptAssist the go-to platform for prompt engineering.
                </p>

                <div className="bg-white/5 rounded-xl p-6 my-8">
                  <h3 className="text-xl font-semibold mb-4">Join Us on This Journey</h3>
                  <p className="mb-4">
                    Whether you're a beginner or an expert, PromptAssist is here to help you master the art of prompt engineering. Start your journey today and discover how our platform can transform your AI interactions.
                  </p>
                  <Link 
                    to="/pricing" 
                    className="inline-flex items-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    Get Started
                    <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
              
              <Link 
                to="/blog/indie-game-revolution" 
                className="block group mb-6"
              >
                <div className="relative h-48 w-full mb-4 rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=2071&auto=format&fit=crop" 
                    alt="Indie Game Development" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-400 transition-colors">
                  The Indie Game Revolution
                </h3>
                <p className="text-muted-foreground mb-3">
                  How solo developers are making millions with their games.
                </p>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>8 min read</span>
                </div>
              </Link>

              <Link 
                to="/blog/advanced-copilot-patterns" 
                className="block group mb-6"
              >
                <div className="relative h-48 w-full mb-4 rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop" 
                    alt="GitHub Copilot" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-400 transition-colors">
                  Advanced Prompt Patterns for GitHub Copilot
                </h3>
                <p className="text-muted-foreground mb-3">
                  Learn how to create effective prompts that maximize Copilot's potential.
                </p>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>4 min read</span>
                </div>
              </Link>

              {/* Newsletter Signup */}
              <div className="mt-8 p-6 bg-white/5 rounded-xl border border-white/10">
                <h3 className="text-lg font-semibold mb-3">Stay Updated</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get the latest articles and tips about prompt engineering delivered to your inbox.
                </p>
                <div className="flex gap-2">
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ThePromptAssistStory; 